import { Badge, Box, Button, Group, NumberInput, Select, Stack, Text, Textarea } from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { CityQuestRO } from '@routellers/types/dist/city-quest/city-quest.ro';
import { CityQuestDisplayTypeEnum } from '@routellers/types/dist/city-quest/enum/city-quest-display-type.enum';
import { CityQuestStatusEnum } from '@routellers/types/dist/city-quest/enum/city-quest-status.enum';
import { Languages } from '@routellers/types/dist/shared/enum/languages.enum';
import { ImageTypeEnum } from '@routellers/types/dist/upload/enum/image-type.enum';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { api, apiErrorMessage } from '../../api';
import { EditorFooter, MediaDropzone, Section } from '../../components/editor';
import { enumOptions } from '../../i18n';
import { AuthorSelect } from '../author-select';
import { CitySearchSelect } from '../city-search-select';

type Props = {
    quest: CityQuestRO;
    onUpdated: (quest: CityQuestRO) => void;
    onClose: () => void;
    onDirtyChange: (dirty: boolean) => void;
};

type ValidationResult = {
    errors: string[];
    warnings: string[];
    playableLanguages: Languages[];
};

const initialValues = (quest: CityQuestRO) => ({
    status: quest.status || null,
    cityId: quest.cityId ?? null,
    localePrimary: quest.localePrimary || null,
    createdByPartnerId: quest.createdByPartnerId ?? null,
    rejectionReason: quest.rejectionReason || '',
    coverUrl: quest.coverUrl || null,
    navigatorQuestroomId: quest.navigatorQuestroomId ?? ('' as number | ''),
    displayType: quest.displayType ?? null,
    distanceKmApprox: quest.distanceKmApprox ?? ('' as number | ''),
    durationTargetMinutes: quest.durationTargetMinutes ?? ('' as number | ''),
    durationRangeMinMinutes: quest.durationRangeMinMinutes ?? ('' as number | ''),
    durationRangeMaxMinutes: quest.durationRangeMaxMinutes ?? ('' as number | ''),
});

const numberOrNull = (value: number | '') => (value === '' ? null : value);

export const QuestOverviewTab = ({ quest, onUpdated, onClose, onDirtyChange }: Props) => {
    const { t } = useTranslation();
    const [saving, setSaving] = useState(false);
    const [marking, setMarking] = useState(false);
    const [validating, setValidating] = useState(false);
    const [validation, setValidation] = useState<ValidationResult | null>(null);

    const statusOptions = useMemo(
        () => enumOptions(Object.values(CityQuestStatusEnum), t, 'enums.cityQuestStatus'),
        [t],
    );
    const languageOptions = useMemo(
        () => enumOptions(Object.values(Languages), t, 'enums.contentLanguage'),
        [t],
    );
    const displayTypeOptions = useMemo(
        () => enumOptions(Object.values(CityQuestDisplayTypeEnum), t, 'enums.cityQuestDisplayType'),
        [t],
    );

    const form = useForm({ initialValues: initialValues(quest) });

    useEffect(() => {
        form.setValues(initialValues(quest));
        form.resetDirty();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [quest]);

    useEffect(() => {
        onDirtyChange(form.isDirty());
    }, [form, onDirtyChange]);

    const save = async () => {
        if (!form.values.status) {
            notifications.show({
                color: 'red',
                message: t('editors.quest.overview.statusRequired'),
            });

            return;
        }

        setSaving(true);

        try {
            await api.cityQuestsApi.update({
                id: quest.id,
                data: {
                    status: form.values.status,
                    cityId: form.values.cityId,
                    localePrimary: form.values.localePrimary || undefined,
                    coverUrl: form.values.coverUrl,
                    navigatorQuestroomId: numberOrNull(form.values.navigatorQuestroomId),
                    displayType: form.values.displayType,
                    distanceKmApprox: numberOrNull(form.values.distanceKmApprox),
                    durationTargetMinutes: numberOrNull(form.values.durationTargetMinutes),
                    durationRangeMinMinutes: numberOrNull(form.values.durationRangeMinMinutes),
                    durationRangeMaxMinutes: numberOrNull(form.values.durationRangeMaxMinutes),
                    createdByPartnerId: form.values.createdByPartnerId,
                    rejectionReason: form.values.rejectionReason.trim() || null,
                },
            });

            const { data: refreshed } = await api.cityQuestsApi.getOne(quest.id);

            if (refreshed) {
                onUpdated(refreshed);
                form.setValues(initialValues(refreshed));
                form.resetDirty();
            }
            notifications.show({ color: 'green', message: t('editors.quest.overview.saved') });
        } catch (error) {
            notifications.show({ color: 'red', message: apiErrorMessage(error) });
        } finally {
            setSaving(false);
        }
    };

    const validate = async () => {
        setValidating(true);

        try {
            const { data } = await api.cityQuestsApi.validate(quest.id);

            if (data) setValidation(data);
        } catch (error) {
            notifications.show({ color: 'red', message: apiErrorMessage(error) });
        } finally {
            setValidating(false);
        }
    };

    const markReady = async () => {
        const ok = window.confirm(t('editors.quest.overview.markReadyConfirm'));

        if (!ok) return;

        setMarking(true);

        try {
            const { data } = await api.cityQuestsApi.markReady(quest.id);

            if (data) onUpdated(data);
            notifications.show({
                color: 'green',
                message: t('editors.quest.overview.markedReady'),
            });
        } catch (error) {
            notifications.show({ color: 'red', message: apiErrorMessage(error) });
        } finally {
            setMarking(false);
        }
    };

    return (
        <Stack gap='lg' pb={80}>
            <Box
                style={{
                    display: 'grid',
                    gridTemplateColumns: 'minmax(260px, 0.9fr) minmax(340px, 1.2fr)',
                    gap: 28,
                    alignItems: 'start',
                }}
            >
                <Section
                    boxed
                    title={t('editors.quest.overview.cover.title')}
                    description={t('editors.quest.overview.cover.description')}
                >
                    <MediaDropzone
                        label=''
                        kind='image'
                        imageType={ImageTypeEnum.CITY_READY_ROUTE}
                        allowUrl={false}
                        value={form.values.coverUrl}
                        onChange={(url) => form.setFieldValue('coverUrl', url)}
                    />
                </Section>

                <Stack gap='lg'>
                    <Section
                        boxed
                        title={t('editors.quest.overview.catalog.title')}
                        description={t('editors.quest.overview.catalog.description')}
                    >
                        <Select
                            label={t('editors.quest.overview.catalog.status')}
                            data={statusOptions}
                            {...form.getInputProps('status')}
                        />
                        <CitySearchSelect
                            value={form.values.cityId}
                            onChange={(cityId) => form.setFieldValue('cityId', cityId)}
                            initialLabel={quest.cityTitle}
                        />
                        <Select
                            label={t('editors.quest.overview.catalog.primaryLanguage')}
                            description={t('editors.quest.overview.catalog.primaryLanguageHint')}
                            data={languageOptions}
                            value={form.values.localePrimary}
                            onChange={(v) =>
                                form.setFieldValue('localePrimary', (v as Languages) || null)
                            }
                        />
                        <AuthorSelect
                            label={t('editors.quest.overview.catalog.author')}
                            description={t('editors.quest.overview.catalog.authorHint')}
                            value={form.values.createdByPartnerId}
                            onChange={(author) =>
                                form.setFieldValue('createdByPartnerId', author?.id ?? null)
                            }
                        />
                        <Select
                            label={t('editors.quest.overview.catalog.displayType')}
                            description={t('editors.quest.overview.catalog.displayTypeHint')}
                            data={displayTypeOptions}
                            clearable
                            value={form.values.displayType}
                            onChange={(v) =>
                                form.setFieldValue(
                                    'displayType',
                                    (v as CityQuestDisplayTypeEnum) || null,
                                )
                            }
                        />
                        <NumberInput
                            label={t('editors.quest.overview.catalog.navigatorQuestroomId')}
                            description={t(
                                'editors.quest.overview.catalog.navigatorQuestroomIdHint',
                            )}
                            min={1}
                            allowDecimal={false}
                            {...form.getInputProps('navigatorQuestroomId')}
                        />
                        <Textarea
                            label={t('editors.quest.overview.catalog.rejectionReason')}
                            minRows={3}
                            autosize
                            {...form.getInputProps('rejectionReason')}
                        />
                    </Section>

                    <Section
                        boxed
                        title={t('editors.quest.overview.routeFacts.title')}
                        description={t('editors.quest.overview.routeFacts.description')}
                    >
                        <NumberInput
                            label={t('editors.quest.overview.routeFacts.distance')}
                            decimalScale={1}
                            step={0.1}
                            min={0}
                            {...form.getInputProps('distanceKmApprox')}
                        />
                        <NumberInput
                            label={t('editors.quest.overview.routeFacts.duration')}
                            min={0}
                            {...form.getInputProps('durationTargetMinutes')}
                        />
                        <Group grow>
                            <NumberInput
                                label={t('editors.quest.overview.routeFacts.durationMin')}
                                min={0}
                                {...form.getInputProps('durationRangeMinMinutes')}
                            />
                            <NumberInput
                                label={t('editors.quest.overview.routeFacts.durationMax')}
                                min={0}
                                {...form.getInputProps('durationRangeMaxMinutes')}
                            />
                        </Group>
                    </Section>
                </Stack>
            </Box>

            {validation && (
                <Section
                    boxed
                    title={t('editors.quest.overview.validation.title')}
                    description={t('editors.quest.overview.validation.description')}
                >
                    {!validation.errors.length && !validation.warnings.length && (
                        <Text size='sm' c='green'>
                            {t('editors.quest.overview.validation.noIssues')}
                        </Text>
                    )}
                    {validation.errors.map((error, index) => (
                        <Text key={index} size='sm' c='red'>
                            {error}
                        </Text>
                    ))}
                    {validation.warnings.map((warning, index) => (
                        <Text key={index} size='sm' c='orange'>
                            {warning}
                        </Text>
                    ))}
                    <Group gap='xs'>
                        <Text size='sm' fw={600}>
                            {t('editors.quest.overview.validation.playableLanguages')}
                        </Text>
                        {validation.playableLanguages.length ? (
                            validation.playableLanguages.map((lang) => (
                                <Badge key={lang} variant='light'>
                                    {lang.toUpperCase()}
                                </Badge>
                            ))
                        ) : (
                            <Text size='sm' c='dimmed'>
                                {t('editors.quest.overview.validation.nonePlayable')}
                            </Text>
                        )}
                    </Group>
                </Section>
            )}

            <EditorFooter
                onCancel={onClose}
                onSave={save}
                saving={saving}
                left={
                    <Group gap='xs'>
                        <Button
                            variant='light'
                            color='green'
                            loading={marking}
                            onClick={markReady}
                        >
                            {t('editors.quest.overview.markReady')}
                        </Button>
                        <Button variant='light' loading={validating} onClick={validate}>
                            {t('editors.quest.overview.validate')}
                        </Button>
                    </Group>
                }
            />
        </Stack>
    );
};
